/**
 * Session lifecycle kind — parsed from `agent:<id>:<rest>` (JAISIU-4605).
 *
 * KIND describes how the session was created (main / fork / cron / subagent /
 * channel), not transport scope. Scope (DM vs group vs thread) stays on the
 * session key body and is surfaced separately when needed.
 */
import { isSubagentSessionKey, parseAgentSessionKey } from "./session-key-utils.js";

export type SessionLifecycleKind =
  | "main"
  | "fork"
  | "subagent"
  | "cron"
  | "channel"
  | "other";

const CHANNEL_PREFIXES = [
  "discord:",
  "slack:",
  "telegram:",
  "web:",
  "webchat:",
  "whatsapp:",
  "signal:",
  "matrix:",
  "instagram:",
  "line:",
] as const;

/** Classify a gateway session key into a lifecycle kind for UI + policy. */
export function classifySessionLifecycleKind(sessionKey: string): SessionLifecycleKind {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return "other";
  }
  if (isSubagentSessionKey(trimmed)) {
    return "subagent";
  }
  const parsed = parseAgentSessionKey(trimmed);
  if (!parsed) {
    return trimmed === "main" ? "main" : "other";
  }
  const rest = parsed.rest;
  if (rest === "main" || rest.startsWith("main:")) {
    return "main";
  }
  if (rest.startsWith("fork:")) {
    return "fork";
  }
  if (rest.startsWith("cron:") || rest.includes(":cron:")) {
    return "cron";
  }
  const lower = rest.toLowerCase();
  if (CHANNEL_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return "channel";
  }
  return "other";
}

/** Short UI label for a lifecycle kind. */
export function sessionLifecycleKindLabel(kind: SessionLifecycleKind): string {
  switch (kind) {
    case "main":
      return "Main";
    case "fork":
      return "Fork";
    case "subagent":
      return "Subagent";
    case "cron":
      return "Cron";
    case "channel":
      return "Channel";
    default:
      return "Session";
  }
}
